import { ArrowRight, Paperclip, Shield, Calendar, User } from "lucide-react";

const formatStop = (details, fallbackCity) => {
  const companyOrName = details?.company || details?.name || "";
  const street = details?.address || "";
  const locality = [details?.cap, details?.city || fallbackCity, details?.province ? `(${details.province})` : ""].filter(Boolean).join(" ");
  return { title: companyOrName || street || fallbackCity || "-", address: street || "", locality: locality || fallbackCity || "-" };
};

const totalColli = (job) => (job?.colli || []).reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) || 0;
const totalPallet = (job) => (job?.colli || []).filter((c) => (c.tipo || "").toLowerCase() === "pallet").reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) || 0;
export const capacityLabel = (job) => {
  const parts = [];
  const pallet = totalPallet(job);
  const colli = totalColli(job);
  if (pallet > 0) parts.push(`${pallet} pallet`);
  if (colli > 0) parts.push(`${colli} colli`);
  if (job?.total_weight) parts.push(`${job.total_weight} kg`);
  return parts.join(" · ") || "0 colli";
};

const readyLabel = (job) => (job.goods_ready_status === "pronta" ? (job.goods_ready_time ? `Pronta alle ${job.goods_ready_time}` : "Pronta") : null);
const ddtLabel = (job) => { const n = job.ddt_files?.length || 0; return n ? (n > 1 ? `DDT PDF (${n})` : "DDT PDF") : null; };
const openPdf = (file) => {
  const binary = atob(file.data_base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const blob = new Blob([bytes], { type: file.content_type || "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

function StopCol({ label, stop, accent, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className={`text-[9px] font-bold uppercase tracking-widest ${accent ? "text-[#FF5200]" : "text-zinc-400"}`}>{label}</p>
      <p className="text-xs font-bold text-[#09090B] uppercase truncate">{stop.title}</p>
      {stop.address && <p className="text-[11px] text-zinc-700 truncate">{stop.address}</p>}
      <p className="text-[11px] text-zinc-500 truncate">{stop.locality}</p>
    </div>
  );
}

// Partenza → Destinazione on one row + all useful operational badges (urgenza, servizio, merce pronta, DDT, assicurazione, note).
export default function JobRouteInfo({ job, idx = 0, testPrefix = "job" }) {
  const from = formatStop(job.origin_details, job.origin);
  const to = formatStop(job.dest_details, job.destination);
  const urgent = job.urgency === "urgente";
  const client = job.company || job.client_name;
  return (
    <div className="min-w-0" data-testid={`${testPrefix}-info-${idx}`}>
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2" data-testid={`${testPrefix}-route-${idx}`}>
        <StopCol label="Partenza" stop={from} testId={`${testPrefix}-from-${idx}`} />
        <ArrowRight size={14} className="text-zinc-400 mt-4 flex-shrink-0" />
        <StopCol label="Destinazione" stop={to} accent testId={`${testPrefix}-to-${idx}`} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {urgent && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-sm bg-red-100 text-red-700 border border-red-300" data-testid={`${testPrefix}-urgent-${idx}`}>URGENTE</span>}
        <span className="px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 bg-zinc-50 rounded-sm border border-zinc-200">{capacityLabel(job)}</span>
        {job.service_type && <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-500 bg-zinc-50 rounded-sm border border-zinc-200">{job.service_type}</span>}
        {client && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 bg-blue-50 rounded-sm border border-blue-200 max-w-[180px] truncate"><User size={10} /> {client}</span>}
        {job.scheduled_date && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold text-[#FF5200] bg-orange-50 rounded-sm border border-orange-200"><Calendar size={10} /> {job.scheduled_date.split("-").reverse().join("-")}</span>}
        {readyLabel(job) && <span className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-sm border border-green-200">{readyLabel(job)}</span>}
        {job.insurance && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 bg-sky-50 rounded-sm border border-sky-200"><Shield size={10} /> Assicurata{job.insurance_value ? ` €${job.insurance_value}` : ""}</span>}
        {job.ddt_reference && <span className="px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 bg-zinc-50 rounded-sm border border-zinc-200">DDT {job.ddt_reference}</span>}
        {ddtLabel(job) && <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPdf(job.ddt_files[0]); }} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100 text-zinc-700 text-[10px] font-bold rounded-sm hover:bg-zinc-200"><Paperclip size={10} /> {ddtLabel(job)}</button>}
      </div>
      {job.descrizione_merce && <p className="text-[11px] text-zinc-700 mt-1"><span className="font-bold uppercase text-zinc-500 text-[9px] tracking-widest">Merce:</span> {job.descrizione_merce}</p>}
      {job.note && <p className="text-[11px] text-amber-700 mt-0.5 bg-amber-50 px-1.5 py-0.5 rounded-sm" data-testid={`${testPrefix}-note-${idx}`}><span className="font-bold uppercase text-amber-600 text-[9px] tracking-widest">Note:</span> {job.note}</p>}
    </div>
  );
}
